import React, { useEffect, useState, useMemo } from 'react';
import { ArrowLeft, Play, Bookmark, Clock, Calendar, Star, Info, Tv, Film, BookOpen, Layers, CheckCircle2, Radio, Sparkles } from 'lucide-react';
import { MangaDownloadDialog } from '../components/MangaDownloadDialog.tsx';
import { sourceProvider } from '../../core/SourceProvider.ts';
import { UnifiedMangaChapters, type MangaChapterVariant } from '../components/UnifiedMangaChapters.tsx';
import { getDiscoveryMedia, mediaTitle, getCachedDiscoveryMediaSync, type HomeMedia } from '../../metadata/HomeRecommendations.ts';
import { RecommendationPicker } from '../components/home/RecommendationPicker.tsx';
import { UnifiedAnimeEpisodes } from '../components/UnifiedAnimeEpisodes.tsx';
import { DiscoveryActions } from '../components/DiscoveryActions.tsx';
import { UnifiedBulkDownload } from '../components/UnifiedBulkDownload.tsx';
import type { UnifiedAnimeSnapshot } from '../../anime/UnifiedAnime.ts';
import type { YomiriManga } from '../../types/extension.ts';
import { createAnimeSource } from '../../anime/AnimeRepository.ts';
import { sourceHttpClient } from '../../core/SourceHttp.ts';
import { yomiriDatabase } from '../../storage/YomiriDatabase.ts';
import { NyomiriSkeletonDetail } from '../components/skeletons/NyomiriSkeletons.tsx';
import './discovery-detail.css';

export interface YomiriDetailViewProps {
  initialManga?: YomiriManga;
  onNavigate: (view: string) => void;
  onOpenManga: (id: string, chapterId?: string, manga?: YomiriManga) => void;
}

export function YomiriDetailView({
  onNavigate,
  onOpenManga,
  initialManga,
}: YomiriDetailViewProps) {
  const params = new URLSearchParams(location.search);
  const id = Number(params.get('media'));
  const mangaId = params.get('manga') || initialManga?.id;
  const sourceId = params.get('source') || initialManga?.sourceId;
  const animeId = params.get('anime');
  const animeSourceId = params.get('animeSource');
  const workId = params.get('workId');

  const [mangaRows, setMangaRows] = useState<MangaChapterVariant[]>([]);
  const [sourceManga, setSourceManga] = useState<YomiriManga | undefined>(initialManga);
  // Fast 0ms synchronous in-memory cache initialization to eliminate click lag:
  const [media, setMedia] = useState<HomeMedia | null>(() => {
    if (id) {
      return getCachedDiscoveryMediaSync(id) || null;
    }
    return null;
  });
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [choose, setChoose] = useState(false);
  const [startRequested, setStartRequested] = useState(0);
  const [bulk, setBulk] = useState(false);
  const [snapshot, setSnapshot] = useState<UnifiedAnimeSnapshot>({
    installed: 0,
    completed: 0,
    episodes: [],
    issues: [],
  });
  const [sourceAnchor, setSourceAnchor] = useState<HTMLElement | null>(null);
  const [mangaSourcesRequested, setMangaSourcesRequested] = useState(0);
  const [sourceRevision, setSourceRevision] = useState(0);

  const openSources = () => {
    setSourceAnchor(
      document.activeElement instanceof HTMLElement && document.activeElement.tagName === 'BUTTON'
        ? document.activeElement
        : null
    );
    setChoose(true);
  };

  useEffect(() => {
    let live = true;
    const cached = id ? getCachedDiscoveryMediaSync(id) : null;
    if (cached && !workId && !animeId && !mangaId) {
      setMedia(cached);
    } else if (!media || media.id !== id) {
      setMedia(null);
    }
    setError('');

    const loadItem = async (): Promise<HomeMedia | null> => {
      if (workId) {
        const work = await yomiriDatabase.getWork(workId);
        if (work) {
          return {
            id: work.externalLinks?.anilistId || 0,
            type: (work.type === 'anime' ? 'ANIME' : 'MANGA') as any,
            title: { romaji: work.romajiTitle || work.title, english: work.englishTitle, native: work.nativeTitle },
            coverImage: { large: work.metadata?.coverUrl || '', extraLarge: work.metadata?.coverUrl },
            bannerImage: work.metadata?.bannerUrl,
            description: work.metadata?.description,
            genres: work.metadata?.genres,
            format: work.metadata?.format || (work.type === 'anime' ? 'TV' : 'MANGA'),
            status: work.metadata?.status,
          };
        }
      }

      if (animeId && animeSourceId) {
        const source = createAnimeSource(animeSourceId, sourceHttpClient);
        const anime = await source.getAnime(animeId);
        return {
          id: 0,
          type: 'ANIME' as const,
          title: { romaji: anime.title },
          coverImage: { large: anime.coverUrl || '', extraLarge: anime.coverUrl },
          description: anime.description,
          genres: anime.genres,
          format: 'TV',
          status: anime.status,
        };
      }

      if (mangaId && sourceId) {
        const manga = await sourceProvider.getManga(sourceId, mangaId);
        if (live) setSourceManga(manga);
        return {
          id: 0,
          type: 'MANGA' as const,
          title: { romaji: manga.title },
          coverImage: { large: manga.coverUrl || '' },
          description: manga.description,
          genres: manga.genres,
          format: 'MANGA',
        };
      }

      return getDiscoveryMedia(id);
    };

    void loadItem()
      .then((item) => {
        if (live && item) setMedia(item);
      })
      .catch((cause) => {
        if (live) setError(cause instanceof Error ? cause.message : 'No se pudo cargar la ficha.');
      });

    return () => {
      live = false;
    };
  }, [id, revision, mangaId, sourceId, animeId, animeSourceId, workId]);

  if (error) {
    return (
      <section className="nyomiri-detail">
        <div className="nyomiri-detail-error-card">
          <p role="alert">{error}</p>
          <div className="nyomiri-detail-error-actions">
            <button className="nyomiri-btn nyomiri-btn-primary" onClick={() => setRevision((v) => v + 1)}>
              Reintentar
            </button>
            <button className="nyomiri-btn nyomiri-btn-neutral" onClick={() => onNavigate('home')}>
              Volver a Inicio
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (!media) {
    return <NyomiriSkeletonDetail />;
  }

  const description = media.description?.replace(/<[^>]*>/g, ' ').trim() || 'Sin sinopsis disponible.';
  const statusLabels: Record<string, string> = {
    RELEASING: 'En emisión',
    FINISHED: 'Finalizado',
    NOT_YET_RELEASED: 'Próximamente',
    CANCELLED: 'Cancelado',
    HIATUS: 'En pausa',
  };
  const status = statusLabels[media.status || ''] || 'Sin estado informado';
  const isReleasing = media.status === 'RELEASING';

  const seasonNames: Record<string, string> = {
    WINTER: 'Invierno',
    SPRING: 'Primavera',
    SUMMER: 'Verano',
    FALL: 'Otoño',
  };
  const season = seasonNames[media.season || ''];
  const formattedSeason = season ? (media.seasonYear ? `${season} ${media.seasonYear}` : season) : null;
  const formattedFormat = media.format ? media.format.replaceAll('_', ' ') : (media.type === 'ANIME' ? 'TV' : 'MANGA');

  return (
    <article className="nyomiri-detail">
      {/* Navigation Top Bar */}
      <div className="nyomiri-detail-nav">
        <button
          className="nyomiri-detail-back"
          onClick={() => (history.state?.yomiriDetailFrom ? history.back() : onNavigate('home'))}
          aria-label="Volver atrás"
        >
          <ArrowLeft size={16} aria-hidden="true" />
          <span>Atrás</span>
        </button>
      </div>

      {/* Hero Section: Cinematic Liquid Glass Banner & Poster */}
      <section className={'nyomiri-detail-hero' + (media.bannerImage ? ' has-banner' : '')}>
        {media.bannerImage && (
          <div className="nyomiri-detail-backdrop">
            <img src={media.bannerImage} alt="" aria-hidden="true" />
            <div className="nyomiri-backdrop-gradient" />
          </div>
        )}

        <div className="nyomiri-detail-intro">
          {/* Cover Poster with 3D lift & ambient cherry glow */}
          <div className="nyomiri-detail-cover-wrapper">
            <img
              className="nyomiri-detail-cover"
              src={media.coverImage.extraLarge || media.coverImage.large}
              alt={`Portada de ${mediaTitle(media)}`}
            />
          </div>

          <div className="nyomiri-detail-summary">
            <h1 className="nyomiri-detail-title">{mediaTitle(media)}</h1>

            {/* Romaji & Native subtitles */}
            <p className="nyomiri-detail-alias">
              {[media.title.romaji, media.title.native].filter((t) => t && t !== mediaTitle(media)).join(' · ')}
            </p>

            {/* Badges Pill Row - Clean & Non-redundant */}
            <div className="nyomiri-detail-pill-row">
              {formattedFormat && (
                <span className="nyomiri-badge-pill nyomiri-badge-format">
                  {formattedFormat}
                </span>
              )}
              {status && (
                <span className={`nyomiri-badge-pill nyomiri-badge-status ${isReleasing ? 'is-live' : ''}`}>
                  <span className="nyomiri-status-dot" />
                  {status}
                </span>
              )}
              {media.averageScore && (
                <span className="nyomiri-badge-pill nyomiri-badge-score">
                  <Star size={13} fill="currentColor" />
                  {media.averageScore}%
                </span>
              )}
            </div>

            {/* Synopsis with Smooth Clamping */}
            <p className={'nyomiri-detail-synopsis' + (!expanded ? ' is-clamped' : '')}>
              {description}
            </p>
            {description.length > 280 && (
              <button
                className="nyomiri-detail-expand"
                aria-expanded={expanded}
                onClick={() => setExpanded((v) => !v)}
              >
                {expanded ? 'Mostrar menos ▴' : 'Mostrar más ▾'}
              </button>
            )}

            {/* Genres Tag Cloud */}
            {!!media.genres?.length && (
              <div className="nyomiri-detail-genres">
                {media.genres.map((genre) => (
                  <span key={genre} className="nyomiri-genre-tag">{genre}</span>
                ))}
              </div>
            )}

            {/* Main Action Buttons */}
            <DiscoveryActions
              manga={sourceManga}
              onSources={() => (media.type === 'MANGA' ? setMangaSourcesRequested((n) => n + 1) : openSources())}
              media={media}
              onPlay={() =>
                media.type === 'ANIME'
                  ? setStartRequested((v) => v + 1)
                  : document.querySelector('.nyomiri-detail-columns')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }
              onBulk={() => setBulk(true)}
            />
          </div>
        </div>
      </section>

      {/* Chapters & Episodes List with Side Facts Ficha */}
      <div className="nyomiri-detail-columns">
        <main className="nyomiri-detail-main-col">
          {media.type === 'ANIME' ? (
            <UnifiedAnimeEpisodes
              key={sourceRevision}
              onSnapshot={setSnapshot}
              media={media}
              startRequested={startRequested}
              onLink={openSources}
            />
          ) : (
            <UnifiedMangaChapters
              onRows={setMangaRows}
              sourcesRequested={mangaSourcesRequested}
              media={media}
              sourceManga={sourceManga}
              revision={sourceRevision}
              onLink={openSources}
              onOpenManga={onOpenManga}
            />
          )}
        </main>

        {/* Ficha Técnica Card */}
        <aside className="nyomiri-detail-facts">
          <div className="nyomiri-facts-header">
            <Info size={16} />
            <h2>Detalles de la obra</h2>
          </div>

          <div className="nyomiri-facts-list">
            <div className="nyomiri-fact-item">
              <span className="nyomiri-fact-label">Formato</span>
              <span className="nyomiri-fact-val">{formattedFormat}</span>
            </div>

            <div className="nyomiri-fact-item">
              <span className="nyomiri-fact-label">Estado</span>
              <span className="nyomiri-fact-val">
                <span className={`nyomiri-status-dot ${isReleasing ? 'is-live' : ''}`} />
                {status}
              </span>
            </div>

            {formattedSeason && (
              <div className="nyomiri-fact-item">
                <span className="nyomiri-fact-label">Temporada</span>
                <span className="nyomiri-fact-val">{formattedSeason}</span>
              </div>
            )}

            {media.episodes && (
              <div className="nyomiri-fact-item">
                <span className="nyomiri-fact-label">Episodios totales</span>
                <span className="nyomiri-fact-val">{media.episodes}</span>
              </div>
            )}

            {media.chapters && (
              <div className="nyomiri-fact-item">
                <span className="nyomiri-fact-label">Capítulos</span>
                <span className="nyomiri-fact-val">{media.chapters}</span>
              </div>
            )}

            {media.duration && (
              <div className="nyomiri-fact-item">
                <span className="nyomiri-fact-label">Duración</span>
                <span className="nyomiri-fact-val">{media.duration} minutos</span>
              </div>
            )}

            {media.averageScore && (
              <div className="nyomiri-fact-item">
                <span className="nyomiri-fact-label">Puntuación</span>
                <div className="nyomiri-score-wrap">
                  <span className="nyomiri-fact-val nyomiri-cherry-val">{media.averageScore}%</span>
                  <div className="nyomiri-score-bar">
                    <div className="nyomiri-score-fill" style={{ width: `${media.averageScore}%` }} />
                  </div>
                </div>
              </div>
            )}

            {media.nextAiringEpisode && (
              <div className="nyomiri-fact-item nyomiri-fact-airing">
                <span className="nyomiri-fact-label">Próxima emisión</span>
                <span className="nyomiri-fact-val nyomiri-airing-pill">
                  <Radio size={12} className="nyomiri-pulse" />
                  Ep. {media.nextAiringEpisode.episode} · {new Date(media.nextAiringEpisode.airingAt * 1000).toLocaleDateString('es-AR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            )}

            {media.startDate?.year && (
              <div className="nyomiri-fact-item">
                <span className="nyomiri-fact-label">Año de estreno</span>
                <span className="nyomiri-fact-val">{media.startDate.year}</span>
              </div>
            )}
          </div>
        </aside>
      </div>

      <RecommendationPicker
        item={choose ? media : null}
        anchorEl={sourceAnchor}
        onPick={() => setSourceRevision((v) => v + 1)}
        onClose={() => setChoose(false)}
        onNavigate={onNavigate}
        onOpenManga={onOpenManga}
      />

      {media.type === 'MANGA' ? (
        <MangaDownloadDialog media={media} rows={mangaRows} open={bulk} onClose={() => setBulk(false)} />
      ) : (
        <UnifiedBulkDownload media={media} episodes={snapshot.episodes} open={bulk} onClose={() => setBulk(false)} />
      )}
    </article>
  );
}
